import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { useInView } from "framer-motion";

// =============================================================================
// Animation Variants
// =============================================================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const scaleIn = {
  hidden: { scale: 0.9, opacity: 0 },
  visible: { scale: 1, opacity: 1 },
};

const slideInLeft = {
  hidden: { opacity: 0, x: -30 },
  visible: { opacity: 1, x: 0 },
};

const slideInRight = {
  hidden: { opacity: 0, x: 30 },
  visible: { opacity: 1, x: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

// =============================================================================
// Count Up Hook for Stats
// =============================================================================

function useCountUp(end: number, duration: number = 2000, inView: boolean) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, inView]);

  return count;
}

// =============================================================================
// Feature Data - Vague marketing copy, no implementation details
// =============================================================================

interface Feature {
  title: string;
  description: string;
  icon: string;
  span?: "wide" | "tall" | "large";
}

const FEATURES: Feature[] = [
  {
    title: "Fully Homomorphic Encryption",
    description:
      "Compute on encrypted data without ever decrypting. HVME-FHE enables secure AI inference, confidential analytics, and private computations at scale.",
    icon: "🔮",
    span: "large",
  },
  {
    title: "Encrypted by Default",
    description:
      "Your data never leaves your control. Process sensitive information with confidence knowing it remains protected throughout the entire workflow.",
    icon: "🔐",
    span: "wide",
  },
  {
    title: "Built for AI Workloads",
    description:
      "Native integration with modern AI pipelines. Works seamlessly with the tools and frameworks you already use.",
    icon: "🧠",
  },
  {
    title: "Post-Quantum Ready",
    description:
      "Lattice-based cryptography resistant to quantum attacks. Your data stays protected even as computing evolves.",
    icon: "⚛️",
    span: "tall",
  },
  {
    title: "Your Infrastructure",
    description:
      "Deploy on your terms. Full ownership, no dependencies on third-party data access. You decide where your data lives.",
    icon: "🏛️",
  },
  {
    title: "Run Anywhere",
    description:
      "Cloud, on-premise, or hybrid. One platform that adapts to your infrastructure requirements.",
    icon: "☁️",
  },
  {
    title: "Threshold Decryption",
    description:
      "Distribute trust across multiple parties. No single entity can decrypt without consensus from the required threshold.",
    icon: "🔑",
    span: "wide",
  },
  {
    title: "Autonomous Agents",
    description:
      "Orchestrate complex workflows with intelligent agents that communicate securely. Full auditability and control over every operation.",
    icon: "🕸️",
  },
];

// Stats data
const STATS = [
  { value: 99, suffix: ".9%", label: "Uptime SLA" },
  { value: 10, suffix: "x", label: "Faster Processing" },
  { value: 500, suffix: "+", label: "Enterprise Clients" },
  { value: 1, suffix: "M+", label: "Secure Operations/Day" },
];

// =============================================================================
// Stat Card Component with Count-Up Animation
// =============================================================================

function StatCard({ stat, index }: { stat: typeof STATS[0]; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const count = useCountUp(stat.value, 2000, isInView);

  return (
    <motion.div
      ref={ref}
      variants={scaleIn}
      className="text-center p-6"
    >
      <motion.div
        initial={{ scale: 0.8 }}
        animate={isInView ? { scale: 1 } : { scale: 0.8 }}
        transition={{ duration: 0.5, delay: index * 0.1, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <span className="font-heading font-black text-4xl md:text-5xl text-hvme-primary">
          {count}{stat.suffix}
        </span>
      </motion.div>
      <p className="font-body text-sm text-hvme-text-secondary mt-2">{stat.label}</p>
    </motion.div>
  );
}

// =============================================================================
// Feature Card Component
// =============================================================================

function FeatureCard({ feature, index }: { feature: Feature; index: number }) {
  const spanClasses = {
    wide: "md:col-span-2",
    tall: "md:row-span-2",
    large: "md:col-span-2 md:row-span-2",
  };

  return (
    <motion.div
      variants={fadeInUp}
      whileHover={{ y: -4, borderColor: "rgba(220, 160, 250, 0.4)" }}
      className={`
        relative p-8 rounded-3xl overflow-hidden
        backdrop-blur-xl
        bg-gradient-to-br from-white/[0.08] to-white/[0.02]
        border border-white/[0.12]
        transition-all duration-500
        hover:shadow-[0_8px_32px_rgba(220,160,250,0.15),inset_0_1px_0_rgba(255,255,255,0.1)]
        hover:bg-gradient-to-br hover:from-white/[0.12] hover:to-white/[0.04]
        ${feature.span ? spanClasses[feature.span] : ""}
      `}
      style={{
        boxShadow: "0 4px 24px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.05)",
      }}
    >
      {/* Glass reflection highlight */}
      <div
        className="absolute inset-0 opacity-50 pointer-events-none"
        style={{
          background: "linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)",
        }}
      />

      {/* Icon with scale animation */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        whileInView={{ scale: 1, opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.4, delay: index * 0.1 + 0.2, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="relative text-4xl mb-4 drop-shadow-lg"
      >
        {feature.icon}
      </motion.div>

      {/* Title */}
      <h3 className="relative font-heading font-bold text-xl text-hvme-text-primary mb-3">
        {feature.title}
      </h3>

      {/* Description */}
      <p className="relative font-body text-sm text-hvme-text-secondary leading-relaxed">
        {feature.description}
      </p>

      {/* Decorative gradient corner - enhanced for glass */}
      <div
        className="absolute -top-12 -right-12 w-48 h-48 opacity-20 pointer-events-none blur-2xl"
        style={{
          background:
            "radial-gradient(circle at center, rgba(220, 160, 250, 0.6), transparent 60%)",
        }}
      />

      {/* Bottom edge glow on hover */}
      <motion.div
        className="absolute bottom-0 left-0 right-0 h-px"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        style={{
          background: "linear-gradient(90deg, transparent, rgba(220,160,250,0.5), transparent)",
        }}
      />
    </motion.div>
  );
}

// =============================================================================
// Features Page Component
// =============================================================================

interface FeaturesPageProps {
  onGetStarted?: () => void;
}

export function FeaturesPage({ onGetStarted }: FeaturesPageProps) {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  // Parallax transforms for hero section
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  return (
    <div className="min-h-screen py-20">
      {/* Hero Section with Parallax */}
      <div ref={heroRef} className="mx-auto max-w-7xl px-6 mb-16 overflow-hidden">
        <motion.div
          style={{ y: heroY, opacity: heroOpacity, scale: heroScale }}
          className="text-center max-w-3xl mx-auto"
        >
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="font-heading font-black text-4xl md:text-6xl tracking-tight mb-6"
            style={{
              background: "linear-gradient(135deg, #DCA0FA, #E6BEFA)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Privacy Without Compromise
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="font-body text-lg text-hvme-text-secondary"
          >
            Enterprise-ready infrastructure that puts you in control.
            No tradeoffs between security and performance.
          </motion.p>
        </motion.div>
      </div>

      {/* Stats Section with Count-Up */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
        variants={staggerContainer}
        className="mx-auto max-w-5xl px-6 mb-16"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-3xl bg-hvme-bg-surface border border-hvme-border">
          {STATS.map((stat, index) => (
            <StatCard key={stat.label} stat={stat} index={index} />
          ))}
        </div>
      </motion.div>

      {/* Bento Grid with Stagger Animation */}
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 auto-rows-fr"
        >
          {FEATURES.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </motion.div>
      </div>

      {/* Bottom CTA with Scale Animation */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={scaleIn}
        transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="mx-auto max-w-7xl px-6 mt-20 text-center"
      >
        <motion.p
          variants={fadeIn}
          className="font-body text-hvme-text-muted mb-6"
        >
          Ready to take control of your AI infrastructure?
        </motion.p>
        <motion.button
          onClick={onGetStarted}
          whileHover={{
            scale: 1.03,
            boxShadow: "0 0 40px rgba(220,160,250,0.5), 0 0 80px rgba(220,160,250,0.25)",
          }}
          whileTap={{ scale: 0.97 }}
          className="relative px-8 py-3.5 rounded-full font-body font-semibold bg-hvme-primary text-white transition-all duration-300 overflow-hidden group cursor-pointer"
        >
          <span className="relative z-10">Get Started Free</span>
          {/* Shimmer effect on hover */}
          <motion.div
            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent"
            initial={{ x: "-100%" }}
            whileHover={{ x: "100%" }}
            transition={{ duration: 0.6 }}
          />
        </motion.button>
      </motion.div>
    </div>
  );
}
